import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigurationService, CoursesService, PathsService, PortalConfigurationService, UrlService } from '@draeger-academy/common';
import { CartService } from '@draeger-academy/common/lib/services/cart/cart.service';
import { OrderService } from '@draeger-academy/common/lib/services/order/order.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { CartItem } from 'models/cartItem';
import { Course } from 'models/course';
import { Order } from 'models/order';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment as env } from '../../../../../src/environments/environment';
import { GenericModalService } from '../generic-modal/services/generic-modal.service';

import { AuthenticationService } from '@draeger-academy/common/lib/services/authentication/authentication.service';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { TranslateService } from '@ngx-translate/core';
registerLocaleData(localeDe, 'de');

@Component({
    selector: 'da-checkout-list',
    templateUrl: './checkout-list.component.html',
    styleUrls: ['./checkout-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CheckoutListComponent implements OnInit {
    @Output()
    nextStep: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    emitTotalPrice: EventEmitter<number> = new EventEmitter<number>();
    @Input() isBookingSummary = false;
    public totalPrice = 0;
    public envApi = env.locales;
    public countryName = '';
    public currency: string;
    public language = this._configurationService.getCurrentSite();
    private _currentData = null;
    public coursesLink;
    public cartItem;
    public firstTotalAmount: number;
    public site: string;
    public countriesData: any;
    public preselectedCountry: string;
    public currentCourseId: string;
    private emptyPerson = {
        address: '',
        city: '',
        company: '',
        country: '',
        department: '',
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        salutation: '',
        subscribe: false,
        title: '',
        zipCode: '',
        regionKey: ''
    };

    private _modalId: string;
    @ViewChild('overlay', { static: true }) private _overlay;
    @ViewChild('locationOverlay', { static: true }) private _locationOverlay;
    @ViewChild('requiredQualificationsModal', { static: true }) private _requiredQualificationsModal;
    public showWarningTile = false;

    private _$order: BehaviorSubject<any> = new BehaviorSubject(new Order([]));
    private _$onlyWebinars: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$onlyWishListItems: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$events: BehaviorSubject<any> = new BehaviorSubject([]);
    private _$totalPrice: BehaviorSubject<any> = new BehaviorSubject(0);
    private _$minusPrice: BehaviorSubject<any> = new BehaviorSubject(0);
    private _$location: BehaviorSubject<CartItem> = new BehaviorSubject<CartItem>(null);
    private _$showLoader: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showServerError: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$checkPrerequirements: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$maxSeats: BehaviorSubject<number> = new BehaviorSubject(null);
    private _$requiredQualificationsList: BehaviorSubject<any> = new BehaviorSubject([]);
    
    constructor(
        private _orderService: OrderService,
        private coursesService: CoursesService,
        private cartService: CartService,
        public _urlService: UrlService,
        private router: Router,
        private _translateService: TranslateService,
        private _localizeRouterService: LocalizeRouterService,
        private _genericModalService: GenericModalService,
        private authenticationService: AuthenticationService,
        private _configurationService: ConfigurationService,
        private _pathsService: PathsService,
        private _portalConfigurationService: PortalConfigurationService

    ) {}

    public get requiredQualificationsList$(): Observable<any> {
        return this._$requiredQualificationsList.asObservable();
    }

    public get order$(): Observable<Order> {
        return this._$order.asObservable();
    }

    public get onlyWebinars$(): Observable<boolean> {
        return this._$onlyWebinars.asObservable();
    }
    public get onlyWishListItems$(): Observable<boolean> {
        return this._$onlyWishListItems.asObservable();
    }

    public get events$(): Observable<Course[]> {
        return this._$events.asObservable();
    }

    public get totalPrice$(): Observable<number> {
        return this._$totalPrice.asObservable();
    }

    public get maxSeats$(): Observable<number> {
        return this._$maxSeats.asObservable();
    }

    public get minusPrice$(): Observable<number> {
        return this._$minusPrice.asObservable();
    }

    public get location$(): Observable<CartItem> {
        return this._$location.asObservable();
    }

    public get showServerError$(): Observable<boolean> {
        return this._$showServerError.asObservable();
    }

    public get showLoader$(): Observable<boolean> {
        return this._$showLoader.asObservable();
    }

    public get checkPrerequirements$(): Observable<boolean> {
        return this._$checkPrerequirements.asObservable();
    }

    private _$isLoggedInUser: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get isLoggedInUser$(): Observable<boolean> {
        return this._$isLoggedInUser.asObservable();
    }

    private _$dateFormat: BehaviorSubject<string> = new BehaviorSubject('');
    public get dateFormat$(): Observable<string> {
        return this._$dateFormat.asObservable();
    }

    private _$pricePattern: BehaviorSubject<string> = new BehaviorSubject('');
    public get pricePattern$(): Observable<string> {
        return this._$pricePattern.asObservable();
    }
    private _$formattedTotalPrice: BehaviorSubject<string> = new BehaviorSubject('');
    public get formattedTotalPrice$(): Observable<string> {
        return this._$formattedTotalPrice.asObservable();
    }

    private _$formattedPrice: BehaviorSubject<string> = new BehaviorSubject('');
    public get formattedPrice$(): Observable<string> {
        return this._$formattedPrice.asObservable();
    }

    private _$formattedExternalPrice: BehaviorSubject<string> = new BehaviorSubject('');
    public get formattedExternalPrice$(): Observable<string> {
        return this._$formattedExternalPrice.asObservable();
    }

    ngOnInit() {
        this.countriesData = this._configurationService.$allCountriesByLanguage.getValue();
        this.preselectCountry();
        window.scrollTo(0, 0);
        this.getCartItems();
        this.coursesLink = this._pathsService.getCourseListLink();
        this.getOrder();
        const _isPortal = this._configurationService.isPartnerPage();

        this._configurationService.portalVersion$.subscribe((portalVersion) => {
            this._userIsLoggedIn(_isPortal, portalVersion);
            this._checkIfWarningShouldBeShown(_isPortal, portalVersion);
        });

        if (_isPortal) {
            this._portalConfigurationService.loadPortalPreferences(this.site ).then((preferences) => {
                const _dateFormat = this._portalConfigurationService.getPortalDateFormat(preferences);
                this._$dateFormat.next(_dateFormat);
            });
        } else {
            this._$dateFormat.next('dd.MM.yyyy');
        }

        this.authenticationService.loggedIn().subscribe((data) => {
            if (data) {
                this._$isLoggedInUser.next(true);
            }
        });

        this.events$.subscribe((events) => {
            this._getRequiredQualificationsList(events);
        });

        this.coursesService.languageSelected.subscribe((language) => {
            setTimeout(() => {
                this._getRequiredQualificationsList(this._$events.getValue());                
            }, 0);
        });

    }

    private _getRequiredQualificationsList(events) {        
            const list = [];
            events.forEach((event) => {
                if (event.bookable === false) {
                    const _event = event;
                    const translation = this._translateService.instant('booking.checkoutList.requiredQualificationsText');
                    const originalHtml = translation.split('{{name}}').join(`<strong>${event.courseName}</strong>`);
                    const noCourseTranslation = this._translateService.instant('booking.checkoutList.requiredQualificationsNoCourseText');  
                    _event['requiredQualificationsNoCourseText'] = noCourseTranslation.split('{{name}}').join(`<strong>${event.courseName}</strong>`);
                    _event['requiredQualificationsText'] = originalHtml.split('{{requiredQualificationsLink}}');
                    _event['rqCourses'] = _event.requiredQualifications.flatMap(rq => rq.mediatingCourses);
                    list.push(_event);

                }
            });         
            this._$requiredQualificationsList.next(list);
    }

    openRequiredQualificationsModal(id: string) {
        this.currentCourseId = id;
        this._modalId = this._genericModalService.create(this._requiredQualificationsModal);
        this._genericModalService.open(this._modalId, this._requiredQualificationsModal, true);
    }

    getOrder() {
        this._orderService.order$.subscribe((order) => {
            if (order && order.cartItems && order.cartItems.length > 0) {
                this._$order.next(order);
                const _order = order;
                this._$events.next(order.cartItems);
                this._calculateTotalPrice(order.cartItems);
                if (!_order.isGroupBooking && (!_order.participants || (_order.participants && _order.participants.length === 0))) {
                    this._setEmptyParticipants(_order);
                }
            } else if (this.cartService.ordersent === false && order?.cartItems) {
                this.getCartItems();
            }
            if (order && order.cartItems) {
            this._findMaxLimitForNumberOfParticipant(order.cartItems);
            }
             if (order && order.participants) {
                if (order.participants.length > this._$maxSeats.getValue() ) {
                    order.participants.length = this._$maxSeats.getValue()
                }
             }
        });
    }

    public getCartItems() {
        let _site = this._configurationService.getCurrentSite();
        this.site = _site;
        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    _site = portalId;
                    this.site = _site;
                }
            });
        }
        this.currency = localStorage.getItem(`currencyOfCartItems${this.site}`);

        let cartItems;
        let tempOrder;

        if (localStorage.getItem(`cartItems${_site}`)) {
            cartItems = JSON.parse(this._urlService.decode(localStorage.getItem(`cartItems${_site}`)));
        }

        if (
            (cartItems && cartItems.events && cartItems.events.length > 0) ||
            (cartItems && cartItems.courses && cartItems.courses.length > 0) ||
            (cartItems && cartItems.wishlist && cartItems.wishlist.length > 0)
        ) {
            this._$showLoader.next(this.isBookingSummary ? false : true);
            this.coursesService
                .getItemsFromCart(cartItems)
                .then((data) => {
                    this._$showLoader.next(false);
                    if (data && data.status === 500) {
                        this._$showServerError.next(true);
                    } else {
                        this._$showServerError.next(false);
                        this._$events.next(data);
                        let _order = null;

                        const _orderSub = this._orderService.order$.subscribe((order) => {
                            _order = order;

                            if (_order) {
                                _order.cartItems = data;
                                this._findMaxLimitForNumberOfParticipant(_order.cartItems)
                            }
                            if (_order && _order.cartItems && _order.cartItems.length > 0) {
                                // this.order = order;
                                this._calculateTotalPrice(_order.cartItems);

                                if (!_order.participants || (_order.participants && _order.participants.length === 0)) {
                                    this._setEmptyParticipants(_order);
                                }

                                // this._$order.next(_order);
                            } else {
                                if (localStorage.getItem(`order${_site}`)) {
                                    tempOrder = JSON.parse(this._urlService.decode(localStorage.getItem(`order${_site}`)));
                                }
                                if (tempOrder) {
                                    _order.user = tempOrder.user;
                                    if (tempOrder.billing) {
                                        _order.billing = tempOrder.billing;
                                    } else {
                                        _order.billing = tempOrder.user;
                                    }
                                    _order.participants = tempOrder.participants;
                                    _order.requestCreated = tempOrder.requestCreated;
                                } else {
                                    if (_order) {
                                        _order.user = null;
                                        _order.billing = null;
                                    }
                                    if (
                                        (_order && !_order.participants) ||
                                        (_order && _order.participants && _order.participants.length === 0)
                                    ) {
                                        this._setEmptyParticipants(_order);
                                    }
                                    if (!_order) {
                                        _order = {
                                            cartItems: data,
                                            user: null,
                                            billing: null,
                                            participants: [],
                                            sameAsBookingContact: true
                                        };
                                        this._setEmptyParticipants(_order);
                                    }
                                }
                                // this._$order.next(_order);
                            }
                        });

                        if (cartItems && cartItems.length) {
                            localStorage.setItem(`itemsNumber${_site}`, cartItems.length.toString());
                            this._calculateTotalPrice(data);
                        }

                        this._$order.next(_order);
                        this._orderService.updateOrder(_order);
                        _orderSub.unsubscribe();
                    }
                })
                .catch((error) => {
                    console.log(error);
                    if (this._localizeRouterService) {
                        const translatedPath: any = this._localizeRouterService.translateRoute('/' + 'server-error-page');
                        this.router.navigate([translatedPath], { skipLocationChange: true });
                    }
                });
        }
    }

    nextClick() {
        this.cartService.activeStep(1);
        this.nextStep.emit('1');
    }

    remove(i, $event: any) {
        $event.preventDefault();
        const _order = this._$order.getValue();
        if (_order && _order.cartItems.length === 1) {
            this.showWarning();
        } else if (_order && _order.cartItems.length > 1) {
            this.coursesService.removeItem(_order.cartItems, _order.cartItems[i]).then((updatedCourses) => {
                this.cartService.removeFromCartItem(_order.cartItems[i]);
                _order.cartItems = updatedCourses;
                this._calculateTotalPrice(updatedCourses);
                this._$events.next(updatedCourses);
                this._$order.next(_order);
                this._orderService.updateOrder(_order);
                this._findMaxLimitForNumberOfParticipant(_order.cartItems);
                window.location.reload();
            });
        }
    }

    public showWarning(): void {
        this._overlayHandler();
    }

    public cancle() {
        this.closeModal();
    }

    public closeModal() {
        this._genericModalService.close(this._modalId);
    }

    public cancleOrder() {
        this._$order.next([]);
        this.closeModal();
        this._orderService.cancleOrder();
        const getCoursesLink = this._pathsService.getCourseListLink();
        const translatedPath: any = this._localizeRouterService.translateRoute('/' + 'courses');
        this.router.navigate([getCoursesLink]);
    }

    increaseParticipants() {
        this._setCountriesAndSalutations();
        const _order = this._$order.getValue();
        const participantEmpty = {
            person: this.emptyPerson,
            sameAs: false
        };
        let participants = null;
        if (_order.participants) {
            participants = _order.participants;
        } else {
            participants = [];
        }

        if (participants && participants.length < this._$maxSeats.getValue()) {
            participants.push(participantEmpty);
            _order.participants = participants;
            this._calculateTotalPrice(_order.cartItems);
            this._$order.next(_order);
            this._orderService.updateOrder(_order);
            this._findMaxLimitForNumberOfParticipant(_order.cartItems);
        }
    }

    decreaseParticipants() {
        const _order = this._$order.getValue();
        if (_order.participants && _order.participants.length > 1) {
            _order.participants = _order.participants.filter((item, index) => index !== _order.participants.length - 1);
            this._calculateTotalPrice(_order.cartItems);
            this._$order.next(_order);
            this._orderService.updateOrder(_order);
            this._findMaxLimitForNumberOfParticipant(_order.cartItems);
        }
    }

    private _setEmptyParticipants(order: Order) {
        this._setCountriesAndSalutations();
        const _order = order;
        const participantEmpty = {
            person: this.emptyPerson,
            sameAs: true
        };

        _order.participants = [];
        _order.participants.push(participantEmpty);
        this._$order.next(_order);
    }

    private async _overlayHandler(): Promise<void> {
        this._modalId = this._genericModalService.create(this._overlay);
        this._genericModalService.open(this._modalId, this._overlay, true);
    }

    public openOverlay(): void {
        this._overlayHandler();
    }

    public openLocationOverlay(event: CartItem): void {
        this._configurationService.$siteSettings.subscribe((data) => {
            if (data && data.length > 0) {
                this.countriesData.filter((obj) => {
                    if (obj.code === event.location.countryKey) {
                        this.countryName = obj.name;
                    }
                });
                this._$location.next(event);

                this._overlayLocationHandler();
            }
        });
    }

    private async _overlayLocationHandler(): Promise<void> {
        this._modalId = this._genericModalService.create(this._locationOverlay);
        this._genericModalService.open(this._modalId, this._locationOverlay, true);
    }

    private _calculateTotalPrice(cartItems: CartItem[]) {
        this._orderService.order$.subscribe((order) => {
            const _order = order;
            let _price = 0;
            if (cartItems) {
                cartItems.forEach((element) => {
                    this.cartItem = element;
                    if (element.externalPrice !== null) {
                        if (!element.isWishItem) {
                            _price = element.externalPrice + _price;
                        }
                    } else {
                        if (!element.isWishItem) {
                            _price = element.coursePrice + _price;
                        }
                    }
                });

                if (_order && _order.participants) {
                    _price = _price * _order.participants.length;
                } else {
                    _price = _price;
                }

                this._$onlyWebinars.next(true);
                for (const cartItem of cartItems) {
                    if (cartItem.courseType !== 'WBT') {
                        this._$onlyWebinars.next(false);
                    }
                }
                this._$onlyWishListItems.next(true);
                for (const cartItem of cartItems) {
                    if (!cartItem.isWishItem) {
                        this._$onlyWishListItems.next(false);
                    }
                }
            }
            this._$totalPrice.next(_price);
            this.formatingTotalPrice();
            this.firstTotalAmount = _price;
            this.emitTotalPrice.emit(this._$totalPrice.getValue());
        });
    }

    private _findMaxLimitForNumberOfParticipant(cartItems){
        const numbersMaxSeats = [];
        if (cartItems) {
            cartItems.forEach((item) => {
                numbersMaxSeats.push(item.seatsMaxExternal);
            });
            this._$maxSeats.next(Math.min.apply(Math, numbersMaxSeats));
        }
    }

    public closeError() {
        this._$showServerError.next(false);
    }

    private getBoolean(value) {
        switch (value) {
            case true:
            case 'true':
            case 1:
            case '1':
            case 'on':
            case 'yes':
                return true;
            default:
                return false;
        }
    }

    public checkPrerequirements(item, i) {
        let itemId = null;

        if (item.externalId) {
            itemId = item.externalId;
        } else {
            itemId = item.courseId;
        }

        if (this.getBoolean(localStorage.getItem(itemId))) {
            return false;
        } else {
            if (itemId) {
                this.authenticationService.loggedIn().subscribe((data) => {
                    if (data) {
                        this.cartService.checkPrerequirementsOfUser(itemId).then((value) => {
                            if (value) {
                                const element = document.getElementById(`row${i}`);
                                element.classList.add('warning');
                            }
                            return value;
                        });
                        return false;
                    } else {
                        return false;
                    }
                });
            } else {
                return false;
            }
        }
    }

    private _checkIfWarningShouldBeShown(isPortal = false, portalVersion: string): void {
        // const _shouldHaveCheckPrerequest =
        //     (!isPortal && this._configurationService.hasFeature('CheckPrerequest')) || (portalVersion === 'v2' && isPortal);
        // this.showWarningTile = _shouldHaveCheckPrerequest;

        if (!isPortal && this._configurationService.hasFeature('CheckPrerequest')) {
            this.showWarningTile = true;
        } else if (portalVersion === 'v2' && isPortal) {
            this._configurationService.getConfigPartnerByPortal().then((data) => {
                if (data) {
                    const tempLanding = data.filter((setting) => setting.key === 'features');
                    let hasCheckPrerequest;
                    if (tempLanding && tempLanding[0] && tempLanding[0].length > 0) {
                        hasCheckPrerequest = tempLanding[0].filter((setting) => setting.key === 'CheckPrerequest');
                    }
                    if (hasCheckPrerequest && hasCheckPrerequest[0]) {
                        this.showWarningTile = hasCheckPrerequest[0].value;
                    }
                }
            });
        }
    }

    public userIsLoggedIn(): boolean {
        return this.authenticationService.isLoggedIn();
    }

    public closeWarning(eventId, courseId, i) {
        let itemId = null;

        if (eventId) {
            itemId = eventId;
        } else if (courseId) {
            itemId = courseId;
        }
        localStorage.setItem(itemId, 'true');
        const element = document.getElementById(`row${i}`);
        if (element) {
            element.classList.remove('warning');
        }

        this.checkPrerequirements(itemId, i);
    }

    getCourseDetailLink(id: string) {
        return this._pathsService.getCourseDetailLink(id);
    }

    getCoursesLink() {
        // return this._pathsService.getCourseListLink();

        if (this._configurationService.isPartnerPage()) {
            this.coursesLink = this._pathsService.getCourseListLink();
            return this.coursesLink;
        }
        return this._localizeRouterService.translateRoute('/courses');
    }

    private _setCountriesAndSalutations() {
        if (!this._configurationService.isPartnerPage()) {
            this._configurationService.$siteSettings.subscribe((data) => {
                if (data) {
                    this.setFormCountriesSalutationRegion(data);
                }
            });
        } else {
            this._configurationService.getConfigPartnerByPortal().then((values) => {
                if (values) {
                    this.setFormCountriesSalutationRegion(values);
                }
            });
        }
    }

    private setFormCountriesSalutationRegion(data) {
        const _regionsData = data.filter((f) => f.key.toLowerCase() === 'regions')[0];
        this._currentData = data;

        this.emptyPerson.salutation = 'Herr';
        this.emptyPerson.country = this.preselectedCountry;
        if (_regionsData && _regionsData['value']) {
            this.emptyPerson.regionKey = _regionsData['value'][0];
        }
    }

    private _userIsLoggedIn(isPortal = false, portalVersion: string): void {
        const _shouldHaveLogin = (!isPortal && this._configurationService.hasFeature('Login')) || (portalVersion === 'v2' && isPortal);
        this.authenticationService.loggedIn().subscribe((data) => {
            this._$isLoggedInUser.next(data && _shouldHaveLogin);
        });
    }

    public findNewAmount(couponCodeList) {
        let _newPrice = this.firstTotalAmount;
        for (const voucher of couponCodeList) {
            _newPrice = _newPrice - voucher.value;
        }
        this._$totalPrice.next(_newPrice);
        this._$minusPrice.next(_newPrice);

        if (this._$totalPrice.getValue() < 0) {
            this._$totalPrice.next(0);
            this.formatingTotalPrice();
        }
        this.emitTotalPrice.emit(this._$totalPrice.getValue());
    }

    private preselectCountry() {
        const isPortal = this._configurationService.isPartnerPage();
        if (!isPortal) {
            const _site = this._configurationService.getCurrentSite();
            if (_site) {
                const _country = _site.split('_')[1].toLocaleUpperCase();
                this.preselectedCountry = _country;
            } else {
                this.preselectedCountry = 'DE';
            }
        } else {
            const countryCode = JSON.parse(localStorage.getItem('portalCountryCode')).countryCode;
            this.preselectedCountry = countryCode.split('_')[1].toLocaleUpperCase();
        }
    }

    private formatingTotalPrice(){      
        if (this._configurationService.isPartnerPage()) {
                this._portalConfigurationService.currencyPattern$.subscribe((currencyPattern) => {
                    this._$formattedTotalPrice.next(this.formatPrice(this._$totalPrice.getValue(), currencyPattern));
                });
        } else {
            this._$pricePattern.next('#.##0,00 " $"');
            this._$formattedTotalPrice.next(this.formatPrice(this._$totalPrice.getValue(), this._$pricePattern.getValue()));
        }
    }

    private formatPrice(price: any, formatPattern){
        if (formatPattern){
            return this._configurationService.formatPrice(price, formatPattern, this.currency);
        } else {
            return price;
        }
    }

    public goToCourseDetails(anchor = '') {
        this.closeModal();
        const _detailPath = this._pathsService.getCourseDetailLink(this.currentCourseId);
        this.router.navigate([_detailPath], { fragment: anchor.split(' ').join('_') });
    }

    public goToContact() {
        this.closeModal();
        const el = document.getElementById('contact-us');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
}
